import type { BatchCounts, StagedBatch, StagedContact } from '../../types/onboarding'

// The batch at a glance, on the dark panel the Weekly and Meetings screens use: what the file held,
// what Scout proposes to do with it, and — first, because it is the reason this screen exists — the
// contacts we already have a two-way relationship with. Nothing here is computed: the API decided
// every action and every flag; this reads the numbers back.

export function Glance({ batch, counts, contacts, recentDays, onPick }: {
  batch: StagedBatch
  counts: BatchCounts
  contacts: StagedContact[]
  recentDays: number
  onPick: (filter: string) => void
}) {
  const flagged = contacts.filter((c) => c.flag.flagged)
  const held = contacts.filter((c) => c.colleagues.length > 0)
  const mapped = [
    batch.mapping.full && ['name', batch.mapping.full],
    batch.mapping.first && ['first name', batch.mapping.first],
    batch.mapping.last && ['last name', batch.mapping.last],
    batch.mapping.email && ['email', batch.mapping.email],
    batch.mapping.company && ['company', batch.mapping.company],
    batch.mapping.title && ['title', batch.mapping.title],
    batch.mapping.phone && ['phone', batch.mapping.phone],
  ].filter(Boolean) as [string, string][]

  return (
    <section className="glance onboard-glance">
      <div className="glance-pipeline">
        <p className="kicker">Waiting on you</p>
        <p className="glance-big">{counts.pending.toLocaleString()}</p>
        <p className="glance-axes">
          <span><strong>{counts.total.toLocaleString()}</strong> staged</span>
          <span><strong>{counts.flagged.toLocaleString()}</strong> flagged</span>
        </p>
        <ActionBars counts={counts} onPick={onPick} />
      </div>

      <div className="glance-words">
        <p className="kicker">What this import is</p>
        <p className="glance-summary">
          {counts.flagged === 0
            ? `Nothing in ${batch.fileName} has two-way contact inside ${recentDays} days. Confirm what you want and it lands; nothing has yet.`
            : `${counts.flagged.toLocaleString()} of these ${counts.total.toLocaleString()} contacts already have a relationship with the firm — they replied to you, or you have sat in a room with them. Look at those before any of this lands anywhere.`}
        </p>
        <dl className="glance-conventions">
          <dt>File</dt>
          <dd>
            <span className="chip">{batch.fileName}</span>
            <span className="chip">{batch.source}</span>
            <span className="chip"><em>{batch.rows.toLocaleString()}</em> rows</span>
            {batch.duplicates > 0 && <span className="chip"><em>{batch.duplicates.toLocaleString()}</em> duplicates folded in</span>}
          </dd>
          <dt>Columns</dt>
          <dd>
            {mapped.map(([field, header]) => <span key={field} className="chip">{field} ← <em>{header}</em></span>)}
            {batch.mapping.unmapped.length > 0 && (
              <span className="chip never" title={batch.mapping.unmapped.join(', ')}>
                <em>{batch.mapping.unmapped.length}</em> kept verbatim
              </span>
            )}
          </dd>
          <dt>Recent</dt>
          <dd>
            <span className="chip">active ≤ {recentDays}d</span>
            <span className="chip">cooling ≤ {recentDays * 2}d</span>
            <span className="chip never">cold beyond</span>
          </dd>
        </dl>
        {held.length > 0 && (
          <p className="glance-collective">
            {held.length === 1 ? 'One of them is' : `${held.length} of them are`} already held by someone else at the firm.
            The row says who, and how strongly — never what they wrote.
          </p>
        )}
      </div>

      <div className="glance-stats onboard-stats">
        <Stat n={counts.new} label="new to Scout" onClick={() => onPick('new')} />
        <Stat n={counts.merge} label="merge into a contact" onClick={() => onPick('merge')} />
        <Stat n={counts.conflict} label="conflicted" tone={counts.conflict > 0 ? 'warn' : undefined} onClick={() => onPick('conflict')} />
        <Stat n={counts.skip} label="already identical" onClick={() => onPick('skip')} />
        <Stat n={counts.accepted} label="accepted" tone={counts.accepted > 0 ? 'up' : undefined} onClick={() => onPick('accepted')} />
        <Stat n={counts.rejected} label="turned down" tone={counts.rejected > 0 ? 'down' : undefined} onClick={() => onPick('rejected')} />
        <div className="glance-busiest">
          <p className="kicker">Look at these first</p>
          {flagged.length === 0 && <p className="muted">Nothing here has answered you or met you.</p>}
          <ul>
            {flagged.slice(0, 4).map((c) => (
              <li key={c.stagedContactId}>
                <button className="link-button" onClick={() => onPick('flagged')}>{c.name}</button>
                <span className="muted">{c.flag.band}{c.flag.meetings > 0 ? ` · ${c.flag.meetings} meeting${c.flag.meetings === 1 ? '' : 's'}` : ''}{c.flag.replies > 0 ? ` · ${c.flag.replies} repl${c.flag.replies === 1 ? 'y' : 'ies'}` : ''}</span>
              </li>
            ))}
            {flagged.length > 4 && <li className="muted small">And {flagged.length - 4} more.</li>}
          </ul>
        </div>
      </div>
    </section>
  )
}

function Stat({ n, label, tone, onClick }: { n: number; label: string; tone?: 'up' | 'down' | 'warn'; onClick?: () => void }) {
  return (
    <button className="glance-stat as-button" onClick={onClick}>
      <strong className={tone ?? ''}>{n.toLocaleString()}</strong>
      <span>{label}</span>
    </button>
  )
}

/** What the import proposes, as a bar per action on a shared scale. Square-root, so one conflict
 *  still reads beside nine hundred new rows. */
function ActionBars({ counts, onPick }: { counts: BatchCounts; onPick: (filter: string) => void }) {
  const rows: { key: string; title: string; n: number; cls: string }[] = [
    { key: 'new', title: 'new', n: counts.new, cls: 'up' },
    { key: 'merge', title: 'merge', n: counts.merge, cls: 'flat' },
    { key: 'conflict', title: 'conflict', n: counts.conflict, cls: 'down' },
    { key: 'skip', title: 'skip', n: counts.skip, cls: 'flat' },
  ]
  const max = Math.max(1, ...rows.map((r) => r.n))
  const w = 260, rowH = 18
  const scale = (n: number) => (Math.sqrt(n) / Math.sqrt(max)) * (w - 96)
  return (
    <svg className="movement-bars" viewBox={`0 0 ${w} ${rows.length * rowH + 6}`} width={w} role="img" aria-label="What the import proposes, per action">
      {rows.map((r, i) => {
        const y = i * rowH + 4
        return (
          <g key={r.key} className="row" onClick={() => onPick(r.key)} style={{ cursor: 'pointer' }}>
            <title>{`${r.n} ${r.title}`}</title>
            <text x={0} y={y + rowH - 9} className="label">{r.title}</text>
            {r.n > 0 && <rect x={62} y={y} width={Math.max(2, scale(r.n))} height={rowH - 8} className={r.cls} rx={2} />}
            <text x={62 + Math.max(2, scale(r.n)) + 6} y={y + rowH - 9} className="count">{r.n}</text>
          </g>
        )
      })}
    </svg>
  )
}
